import { LayersFill } from "@procore/core-icons";
import { useCallback, useMemo, useRef } from "react";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { store } from "app/store";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { ToolbarButtonEntry } from "contexts/toolbars";
import { getBestAxisAlignedView } from "features/orthoCam/utils";
import {
    CameraType,
    renderActions,
    selectCameraType,
    selectDefaultVisibility,
    selectIsSubtreesPanelOpen,
    selectIsXRayActive,
} from "features/render";
import { undoRedoActions } from "features/undoRedo";
import OrthographicIcon from "media/icons/orthographic.svg?react";
import PerspectiveIcon from "media/icons/perspective.svg?react";
import XRayIcon from "media/icons/xray.svg?react";
import { telemetry } from "telemetry";
import { isMacintosh } from "utils/misc";

import { VIEW_TOOLBAR_BUTTON_IDS } from "./utils";

/**
 * Encapsulates all state and button definitions shared between ViewToolbar and
 * the overflow flyout. Returns the three view button entries, the ref to anchor
 * SubtreesPopover to the render-modes button, and the current panel open state.
 */
export function useViewToolbarButtons() {
    const dispatch = useAppDispatch();
    const cameraType = useAppSelector(selectCameraType);
    const isXRayActive = useAppSelector(selectIsXRayActive);
    const isSubtreesPanelOpen = useAppSelector(selectIsSubtreesPanelOpen);
    const subtreesBtnRef = useRef<HTMLButtonElement>(null);
    const {
        state: { view, scene },
    } = useExplorerGlobals(true);

    const handleCameraToggle = useCallback(() => {
        if (!view || !scene) {
            return;
        }
        void track(Events.toolbar.cameraTapped, { origin: EventOrigin.Toolbar });

        if (cameraType === CameraType.Pinhole) {
            getBestAxisAlignedView(view)
                .then((orthographicParams) => {
                    dispatch(renderActions.setCamera({ type: CameraType.Orthographic, goTo: orthographicParams }));
                })
                .catch((error) => {
                    console.warn("Failed to calculate best axis-aligned view:", error);
                    telemetry.trackCaughtException({ source: "useViewToolbarButtons", category: "render", error });
                    dispatch(renderActions.setCamera({ type: CameraType.Orthographic }));
                });
        } else {
            dispatch(renderActions.setCamera({ type: CameraType.Pinhole }));
        }
    }, [dispatch, view, scene, cameraType]);

    const buttons = useMemo(
        (): ToolbarButtonEntry[] => [
            {
                config: {
                    id: VIEW_TOOLBAR_BUTTON_IDS.CAMERA_TOGGLE,
                    icon: cameraType === CameraType.Orthographic ? <PerspectiveIcon /> : <OrthographicIcon />,
                    onClick: handleCameraToggle,
                },
                state: {
                    active: false,
                    disabled: !view || !scene,
                    visible: true,
                },
            },
            {
                config: {
                    id: VIEW_TOOLBAR_BUTTON_IDS.RENDER_MODES,
                    icon: <LayersFill />,
                    ref: subtreesBtnRef,
                    onClick: () => dispatch(renderActions.setSubtreesPanelOpen(!isSubtreesPanelOpen)),
                    tooltip: {
                        toolName: "renderModes",
                        shortcutKey: "",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: isSubtreesPanelOpen,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: VIEW_TOOLBAR_BUTTON_IDS.XRAY_TOGGLE,
                    icon: <XRayIcon />,
                    onClick: () => {
                        const currentVisibility = selectDefaultVisibility(store.getState());
                        dispatch(renderActions.toggleXRayMode());
                        dispatch(
                            undoRedoActions.push({
                                description: "Toggle X-Ray mode",
                                undoCallback: () => dispatch(renderActions.setVisibilityMode(currentVisibility)),
                                redoCallback: () => dispatch(renderActions.toggleXRayMode()),
                            }),
                        );
                    },
                    tooltip: {
                        toolName: "xRay",
                        shortcutKey: isMacintosh ? "Cmd+X" : "Ctrl+X",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: isXRayActive,
                    disabled: !view || !scene,
                    visible: true,
                },
            },
        ],
        [cameraType, view, scene, handleCameraToggle, isXRayActive, isSubtreesPanelOpen, dispatch],
    );

    return { buttons, subtreesBtnRef, isSubtreesPanelOpen };
}
